import React from 'react';
import { Score, fmtScore, poolLabel, numOrNull } from '../ResearchPage';

// THE NEUTRAL EVIDENCE SUMMARY — deliberately NOT a verdict. It lays each input side by side —
// the agent/analyst scores per pool, this name's chatter favourability, and the feeds not yet
// arriving — and stops there. It does not blend them into a buy/sell label: this is a read-only
// shareholder surface, and a single synthesised recommendation would be personalised investment
// advice dressed as a tile. The reader sees what each source says and draws their own line.
//
// A pending feed is shown AS pending, not hidden. Signals (the strategy book) and the technical
// screen are coming from Maghaweer; reserving a labelled, muted tile now means the strip does not
// silently change shape the day they arrive, and a reader can see the summary is not yet complete.

const pctFav = (n) => (n == null ? null : Math.round(n * 100));

function favColor(n) {
  if (n == null) return 'text-gray-400';
  if (n > 0.05) return 'text-green-600 dark:text-green-400';
  if (n < -0.05) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

const BUCKET_NOTE = {
  winners: 'most favoured', losers: 'least favoured',
  movers: 'moving', top_companies: 'most discussed',
};

// One tile. `tone='pending'` mutes it so a not-yet-live feed reads as reserved, not empty.
// min-w-0 on mobile (the 2-col grid sets the width); a fixed min-width only on sm+ where the
// tiles sit in a horizontal row.
function Tile({ label, children, note, tone }) {
  return (
    <div className={`min-w-0 rounded-xl border px-3 py-2.5 sm:min-w-[8.5rem] sm:flex-1 ${tone === 'pending'
      ? 'border-dashed border-gray-200 dark:border-gray-800'
      : 'border-gray-200 dark:border-gray-800'}`}>
      <div className="text-[9.5px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
      <div className="mt-1">{children}</div>
      {note ? <div className="mt-0.5 text-[10px] text-gray-400">{note}</div> : null}
    </div>
  );
}

// Agent → analyst for one pool, matching Quorum's "analyst · agent X.XX" treatment.
function PoolTile({ pool }) {
  return (
    <Tile label={poolLabel(pool.pool)} note={`agent ${fmtScore(pool.score_ai)}`}>
      <Score v={pool.score_human} size="lg" />
    </Tile>
  );
}

export default function EvidenceStrip({ score, chatter, signals, technical }) {
  const pools = (score && Array.isArray(score.pool_scores)) ? score.pool_scores : [];
  const overall = score ? numOrNull(score.overall_score_final) : null;
  const fav = chatter ? numOrNull(chatter.company?.favorability) : null;

  return (
    // Mobile: a 2-column grid so every piece of evidence is visible at once (nothing scrolls off
    // the edge, per the house metric-tile rule). sm+: a single horizontal row.
    <div className="scrollbar-thin grid grid-cols-2 gap-2 sm:-mx-1 sm:flex sm:overflow-x-auto sm:px-1 sm:pb-1">
      {pools.map((p, i) => <PoolTile key={i} pool={p} />)}

      {overall != null && (
        <Tile label="Overall" note="blended">
          <Score v={overall} size="lg" />
        </Tile>
      )}

      <Tile label="Chatter"
            note={chatter ? `${BUCKET_NOTE[chatter.bucket] || 'mentioned'}` : 'not mentioned'}>
        {chatter
          ? <span className={`font-serif text-xl tabular-nums ${favColor(fav)}`}>
              {fav == null ? '—' : `${fav > 0 ? '+' : ''}${pctFav(fav)}`}
              {fav != null && <span className="ml-0.5 text-[10px] font-normal text-gray-400">fav</span>}
            </span>
          : <span className="font-serif text-xl text-gray-300 dark:text-gray-600">—</span>}
      </Tile>

      {/* The feeds still to come. Labelled and muted, not omitted. */}
      <Tile label="Signals" tone="pending" note="strategy book">
        <span className="text-[13px] font-medium text-gray-400">
          {signals ? 'see below' : 'Pending'}
        </span>
      </Tile>
      <Tile label="Technical" tone="pending" note="price screen">
        <span className="text-[13px] font-medium text-gray-400">
          {technical ? 'see below' : 'Pending'}
        </span>
      </Tile>
    </div>
  );
}
